import { reviewPathAfterEdit } from 'library/helpers/importEditFlow';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { notification } from 'antd';
import {
  FiCheck,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiMessageCircle,
  FiTrash2,
  FiTrendingUp,
  FiVideo,
} from 'react-icons/fi';
import PropertyService from 'service/PropertyService';
import { useOnboardingBackConfirm } from 'library/hooks/useOnboardingBackConfirm';
import {
  addImage,
  addStoredImages,
  addVideoUrl,
  addWalkthroughVideoUrl,
  deleteImage,
  setAllowLiveWalkthrough,
  setImages,
  setThumbnail,
  updateOnboardingStage,
} from '../../features/placeSlice';
import { IMAGE_CATEGORY_OPTIONS } from 'settings/constant';
import {
  OnboardingShell,
  Chip,
  ChipRow,
  Field,
  Group,
  SwitchRow,
  ErrorText,
} from 'components/Onboarding';
import {
  DropdownBackdrop,
  DropdownTrigger,
  DropdownPanel,
  DropdownSearch,
  DropdownList,
  DropdownOption,
  DropdownEmpty,
  PhotoGrid,
  PhotoTile,
  TileButton,
  UploadTile,
  Hint,
  PromoBanner,
  VideoDrop,
  VideoRow,
} from './PhotoUpload.styles';

interface LocalImage {
  id: string;
  file: File;
}

interface DeletedImage {
  category: string;
  url: string;
}

interface ImageCategories {
  [imageName: string]: string;
}

type ImageUrlList = Record<string, string[]>;

interface ApiResponse {
  result?: boolean | unknown;
  success?: boolean;
  responseInformation?: {
    responseCode?: number | string;
    responseMessage?: string;
    isSuccess?: boolean;
  };
}

interface VideoUpdateData {
  addVideo: File | null;
  deletedVideoUrl: string;
  propertyId: string | null;
  videoType: 'main' | 'walkthrough';
}

interface VideoUploadData {
  video: File | null;
  onboardingStage: number;
  propertyId: string | null;
  videoType: 'main' | 'walkthrough';
}

/** One photo in the grid: either already saved on the server or picked in this session. */
interface Tile {
  key: string;
  url: string;
  category: string;
  kind: 'server' | 'local';
  localId?: string;
}

/** Quick-pick rooms; "Others" opens the full searchable list of categories. */
const ROOM_PRESETS: { key: string; label: string }[] = [
  { key: 'livingRoom', label: 'Living room' },
  { key: 'bedroom', label: 'Bedroom' },
  { key: 'kitchen', label: 'Kitchen' },
  { key: 'bathroom', label: 'Bathroom' },
  { key: 'balcony', label: 'Balcony' },
  { key: 'privatePool', label: 'Pool' },
  { key: 'exteriorPropertyView', label: 'Exterior' },
  { key: 'viewFromProperty', label: 'View' },
];

const MAX_SIZE = 5 * 1024 * 1024;
const MAX_UPLOAD_COUNT = 50;
const MIN_UPLOAD_COUNT = 5;
const MAX_VIDEO_MB = 50;

const roomLabel = (key: string): string =>
  (IMAGE_CATEGORY_OPTIONS as Record<string, string>)[key] || key;

/**
 * Which room categories to offer, given the room counts the host entered earlier. A category
 * for a room the property doesn't have (e.g. "Bedroom 3" with 2 bedrooms) is left out. A count
 * of `null` means we don't know it (e.g. the details step wasn't visited), so nothing is hidden.
 */
interface RoomCounts {
  bedrooms: number | null;
  bathrooms: number | null;
  balconies: number | null;
}

const isRoomAllowed = (key: string, counts: RoomCounts): boolean => {
  const numbered = /^(bedroom|bathroom)(\d+)$/.exec(key);
  if (numbered) {
    const count = numbered[1] === 'bedroom' ? counts.bedrooms : counts.bathrooms;
    return count === null || Number(numbered[2]) <= count;
  }
  if (key === 'bedroom') return counts.bedrooms === null || counts.bedrooms > 0;
  if (key === 'bathroom') return counts.bathrooms === null || counts.bathrooms > 0;
  if (key === 'balcony') return counts.balconies === null || counts.balconies > 0;
  return true;
};

const normalizeImageUrlForCompare = (url: string): string => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (_) {
    return url.split('?')[0].split('#')[0];
  }
};

const dedupeImageUrlList = (
  imageUrlList: ImageUrlList | undefined,
): ImageUrlList => {
  if (!imageUrlList) return {};
  const out: ImageUrlList = {};
  const globallySeen = new Set<string>();
  Object.keys(imageUrlList).forEach((category) => {
    const categorySeen = new Set<string>();
    const unique = (imageUrlList[category] || []).filter((url) => {
      const normalized = normalizeImageUrlForCompare(url);
      if (!normalized) return false;
      if (categorySeen.has(normalized)) return false;
      if (globallySeen.has(normalized)) return false;
      categorySeen.add(normalized);
      globallySeen.add(normalized);
      return true;
    });
    if (unique.length > 0) {
      out[category] = unique;
    }
  });
  return out;
};

const flatten = (list: ImageUrlList = {}): string[] =>
  Object.values(list).reduce<string[]>((acc, arr) => acc.concat(arr || []), []);

const countImagesInList = (imageUrlList: ImageUrlList = {}): number =>
  flatten(imageUrlList).length;

/** Backend shapes vary: some use responseCode 0, others isSuccess / success / truthy result. */
const isVideoApiSuccess = (res: ApiResponse | null | undefined): boolean => {
  if (!res) return false;
  const ri = res.responseInformation;
  const code = ri?.responseCode;
  if (code === 0 || code === '0' || Number(code) === 0) return true;
  if (ri?.isSuccess === true) return true;
  if (res.success === true) return true;
  if (res.result === true) return true;
  const r = res.result as any;
  if (r && typeof r === 'object' && !Array.isArray(r) && r.videoUrl) return true;
  if (r !== undefined && r !== null && r !== false) {
    if (typeof r === 'number' && r !== 0) return true;
    if (typeof r === 'string' && r.length > 0) return true;
    if (typeof r === 'object' && !Array.isArray(r) && Object.keys(r).length > 0) return true;
  }
  return false;
};

/** Redux `propertyId` defaults to `0`; never treat 0 / "0" as a real listing id. */
const isUsablePropertyId = (v: unknown): boolean => {
  if (v == null || v === '') return false;
  const s = String(v).trim();
  return s.length > 0 && s !== '0' && s !== 'null' && s !== 'undefined';
};

/* ---------------- "Others" room picker ---------------- */

interface CategoryDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: Record<string, string>;
  placeholder?: string;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select a room or area (e.g. Living Room, Bedroom…)',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleOpen = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setOpen(true);
    setSearch('');
  }, []);

  const filtered = Object.entries(options).filter(([, label]) =>
    label.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      {open && <DropdownBackdrop role="presentation" onClick={() => setOpen(false)} />}

      <DropdownTrigger ref={triggerRef} onClick={handleOpen} $open={open} $filled={!!value}>
        <span>{value ? options[value] : placeholder}</span>
        <FiChevronDown size={16} />
      </DropdownTrigger>

      {open && (
        <DropdownPanel style={panelStyle}>
          <DropdownSearch>
            <input
              autoFocus
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </DropdownSearch>
          <DropdownList>
            {filtered.map(([key, label]) => (
              <DropdownOption
                key={key}
                role="option"
                $active={key === value}
                onClick={() => {
                  onChange(key);
                  setOpen(false);
                }}
              >
                {label}
                {key === value && <FiCheck size={15} />}
              </DropdownOption>
            ))}
            {filtered.length === 0 && <DropdownEmpty>No results</DropdownEmpty>}
          </DropdownList>
        </DropdownPanel>
      )}
    </>
  );
};

/* ---------------- Photos & video (one page) ---------------- */

const PhotoUpload: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const hostPropertyId = query.get('id');
  const isEditMode = !!hostPropertyId || query.get('edit') === '1';

  const {
    images,
    thumbnail,
    propertyId,
    videoUrl,
    walkthroughVideoUrl,
    allowLiveWalkthrough: reduxAllowLiveWalkthrough,
    offerings,
    offeringsMap,
  } = useSelector((state: any) => ({
    images: state.images.imageUrlList as ImageUrlList,
    thumbnail: state.images.thumbnail as Record<string, string>,
    propertyId: state.propertyId as string,
    videoUrl: state.videoUrl as string | null,
    walkthroughVideoUrl: state.walkthroughVideoUrl as string | null,
    allowLiveWalkthrough: state.allowLiveWalkthrough as boolean,
    offerings: state.offerings as Record<string, number> | null,
    offeringsMap: state.offeringsMap as Record<string, any> | null,
  }));

  /* The saved listing's room counts: the source of truth when the host reaches this page without
     going through the details step in this session (imported or existing listings). */
  const [savedRooms, setSavedRooms] = useState<{
    offerings: Record<string, number>;
    offeringsMap: Record<string, any>;
  } | null>(null);

  /* Room counts from the property details step. */
  const roomCounts = useMemo<RoomCounts>(() => {
    // The saved listing first, then what the details step left in the store; each room type falls
    // back on its own, so a source that can't name a field never switches the filter off.
    const sources = [
      savedRooms,
      offerings ? { offerings, offeringsMap: offeringsMap || {} } : null,
    ];
    // The details fields have fixed ids (2 Bedrooms, 4 Bathrooms, 5 Balconies), used when a source
    // can't give the field's name.
    const countFor = (name: RegExp, fieldId: string): number | null => {
      for (const source of sources) {
        if (!source) continue;
        const entry = Object.entries(source.offeringsMap).find(([, value]) => {
          const label = typeof value === 'string' ? value : value?.detailsName || value?.name || '';
          return name.test(String(label).trim());
        });
        const raw: unknown = entry ? source.offerings[entry[0]] : source.offerings[fieldId];
        if (raw === undefined || raw === null || raw === '') continue;
        const n = Number(raw);
        if (Number.isFinite(n)) return n;
      }
      return null;
    };
    return {
      bedrooms: countFor(/^bed\s?rooms?$/i, '2'),
      bathrooms: countFor(/^bath\s?rooms?$/i, '4'),
      balconies: countFor(/^balcon(y|ies)$/i, '5'),
    };
  }, [savedRooms, offerings, offeringsMap]);

  /* ---- photos ---- */
  const [localImages, setLocalImages] = useState<LocalImage[]>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [imageCategories, setImageCategories] = useState<ImageCategories>({});
  const [deletedImages, setDeletedImages] = useState<DeletedImage[]>([]);
  const [activeRoom, setActiveRoom] = useState<string>('livingRoom');
  const [othersOpen, setOthersOpen] = useState(false);
  const [extraRooms, setExtraRooms] = useState<string[]>([]);
  /* Photos in the store can be left over from an earlier listing, so only show them once this
     listing's saved photos have been loaded from the server. */
  const [serverPhotosReady, setServerPhotosReady] = useState<boolean>(false);

  /* ---- videos ---- */
  const [video, setVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [walkthroughVideo, setWalkthroughVideo] = useState<File | null>(null);
  const [walkthroughPreview, setWalkthroughPreview] = useState<string | null>(null);
  const [allowLiveWalkthrough, setAllowLiveWalkthroughLocal] = useState<boolean>(
    reduxAllowLiveWalkthrough ?? true,
  );
  const videoInputRef = useRef<HTMLInputElement>(null);
  const walkthroughInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const activeRoomRef = useRef(activeRoom);
  activeRoomRef.current = activeRoom;
  const previewsRef = useRef(previews);
  previewsRef.current = previews;

  const resolvePropertyId = (): string | number | null => {
    if (isUsablePropertyId(hostPropertyId)) return hostPropertyId as string;
    if (isUsablePropertyId(propertyId)) return propertyId;
    const fromLs = localStorage.getItem('newPropertId') || localStorage.getItem('id');
    if (isUsablePropertyId(fromLs)) return fromLs as string;
    return null;
  };

  const { BackConfirmModal, backHandler } = useOnboardingBackConfirm(() => {
    const pid = resolvePropertyId();
    if (isEditMode && pid) navigate(reviewPathAfterEdit(pid));
    else navigate('/host/amenities');
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(
    () => () => {
      Object.values(previewsRef.current).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      });
    },
    [],
  );

  /* Read the room counts saved for this property. */
  useEffect(() => {
    const pid = resolvePropertyId();
    if (!pid) return undefined;
    let cancelled = false;
    Promise.all([
      PropertyService.getPropertyDetailsById(pid),
      PropertyService.getPropertyDetailsFieldList().catch(() => null),
    ])
      .then(([propRes, fieldRes]: any[]) => {
        if (cancelled) return;
        const names: Record<string, string> = {};
        (fieldRes?.result || []).forEach((f: any) => {
          names[String(f.id)] = String(f.name || '');
        });
        const saved: Record<string, number> = {};
        const map: Record<string, any> = {};
        (propRes?.result?.details || []).forEach((d: any) => {
          const id = String(d.propertyDetailsId);
          saved[id] = Number(d.detailsValue);
          map[id] = d.detailsName || d.name || names[id] || '';
        });
        if (Object.keys(saved).length > 0) setSavedRooms({ offerings: saved, offeringsMap: map });
      })
      .catch(() => {
        // keep whatever the details step left in the store
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, propertyId]);

  /* Load what is already saved (photos + videos) for this property. */
  useEffect(() => {
    const pid = resolvePropertyId();
    if (!pid) {
      dispatch(setImages({ imageUrlList: {}, thumbnail: {} }));
      setServerPhotosReady(true);
      return;
    }
    let cancelled = false;
    const hydrate = async () => {
      try {
        const res: any = await PropertyService.getPhotos(pid);
        if (cancelled) return;
        const imageUrlList = dedupeImageUrlList(res?.result?.imageUrlList || {});
        const thumb = res?.result?.thumbnailUrl || res?.result?.thumbnail || {};
        dispatch(setImages({ imageUrlList, thumbnail: thumb }));
        dispatch(addStoredImages(imageUrlList));
      } catch (_) {
        if (cancelled) return;
        // No saved photos for this listing: drop anything left over from another one.
        dispatch(setImages({ imageUrlList: {}, thumbnail: {} }));
      }
      setServerPhotosReady(true);
      try {
        const responseData: any = await PropertyService.getPropertyVideo(pid);
        if (cancelled) return;
        if (responseData?.responseInformation?.responseCode === 0) {
          dispatch(addVideoUrl(responseData?.result?.videoUrl ?? null));
          dispatch(addWalkthroughVideoUrl(responseData?.result?.walkthroughVideoUrl ?? null));
          const alw: boolean = responseData?.result?.allowLiveWalkthrough ?? true;
          dispatch(setAllowLiveWalkthrough(alw));
          setAllowLiveWalkthroughLocal(alw);
        }
      } catch (_) {
        // videos stay empty
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, propertyId]);

  const dedupedImages = dedupeImageUrlList(serverPhotosReady ? images : {});

  /* Every photo, saved ones first, in the order they will be shown. */
  const tiles: Tile[] = [
    ...Object.keys(dedupedImages).flatMap((category) =>
      dedupedImages[category].map<Tile>((url) => ({
        key: `server:${url}`,
        url,
        category,
        kind: 'server',
      })),
    ),
    ...localImages.map<Tile>((img) => ({
      key: `local:${img.id}`,
      url: previews[img.id] || '',
      category: imageCategories[img.id] || activeRoom,
      kind: 'local',
      localId: img.id,
    })),
  ];

  const savedThumbUrl = thumbnail ? Object.values(thumbnail)[0] : undefined;
  const effectiveCoverKey =
    tiles.find((t) => t.kind === 'server' && t.url === savedThumbUrl)?.key ||
    tiles[0]?.key ||
    null;

  const totalPhotos = tiles.length;
  const missing = Math.max(0, MIN_UPLOAD_COUNT - totalPhotos);
  const missingMessage = `${missing} more photo${missing === 1 ? '' : 's'} needed before you can continue.`;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp'] },
    disabled: localImages.length >= MAX_UPLOAD_COUNT,
    onDrop: (files: File[]) => {
      const category = activeRoomRef.current;
      const oversizedFiles = files.filter((f) => f.size > MAX_SIZE);

      if (files.length + localImages.length > MAX_UPLOAD_COUNT) {
        setError(`You can upload a maximum of ${MAX_UPLOAD_COUNT} photos.`);
        return;
      }
      if (oversizedFiles.length > 0) {
        setError(`Some photos exceed 5MB: ${oversizedFiles.map((f) => f.name).join(', ')}`);
        return;
      }
      if (!category) {
        setError('Please select a room or area before adding photos.');
        return;
      }
      setError('');

      const filesWithIds: LocalImage[] = files.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
      }));

      setLocalImages((prev) => [...prev, ...filesWithIds]);
      setImageCategories((prev) => {
        const next = { ...prev };
        filesWithIds.forEach((img) => {
          next[img.id] = category;
        });
        return next;
      });
      setPreviews((prev) => {
        const next = { ...prev };
        filesWithIds.forEach((img) => {
          next[img.id] = URL.createObjectURL(img.file);
        });
        return next;
      });
    },
  });

  const handleDeleteTile = (tile: Tile): void => {
    if (tile.kind === 'server') {
      setDeletedImages((prev) => [...prev, { category: tile.category, url: tile.url }]);
      dispatch(deleteImage({ category: tile.category, url: tile.url }));
      return;
    }
    const id = tile.localId as string;
    try {
      URL.revokeObjectURL(previews[id]);
    } catch (_) {}
    setLocalImages((prev) => prev.filter((img) => img.id !== id));
    setImageCategories((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
    setPreviews((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  /** Moves a photo one position earlier/later among photos of the same kind (display order only). */
  const moveLocal = (tile: Tile, direction: -1 | 1): void => {
    setLocalImages((prev) => {
      const index = prev.findIndex((img) => img.id === tile.localId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleCategoryEdit = (tile: Tile, newCategory: string): void => {
    if (tile.kind === 'local') {
      setImageCategories((prev) => ({ ...prev, [tile.localId as string]: newCategory }));
      return;
    }
    Object.keys(images).forEach((category) => {
      if (images[category].includes(tile.url)) {
        dispatch(deleteImage({ category, url: tile.url }));
        dispatch(addImage({ category: newCategory, imageUrl: tile.url }));
      }
    });
  };

  /* ---- video helpers ---- */

  const acceptVideo = (file: File, kind: 'main' | 'walkthrough'): void => {
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      notification.error({ message: `File size must be under ${MAX_VIDEO_MB}MB` });
      return;
    }
    const url = URL.createObjectURL(file);
    if (kind === 'main') {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
      setVideo(file);
      setVideoPreview(url);
    } else {
      if (walkthroughPreview) URL.revokeObjectURL(walkthroughPreview);
      setWalkthroughVideo(file);
      setWalkthroughPreview(url);
    }
  };

  const clearPickedVideo = (kind: 'main' | 'walkthrough'): void => {
    if (kind === 'main') {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
      setVideo(null);
      setVideoPreview(null);
    } else {
      if (walkthroughPreview) URL.revokeObjectURL(walkthroughPreview);
      setWalkthroughVideo(null);
      setWalkthroughPreview(null);
    }
  };

  const deleteSavedVideo = async (kind: 'main' | 'walkthrough'): Promise<void> => {
    const url = kind === 'main' ? videoUrl : walkthroughVideoUrl;
    if (!url) return;
    const pid = resolvePropertyId();
    if (!pid) {
      notification.error({ message: 'Property id missing', description: 'Please go back and retry.' });
      return;
    }
    if (!window.confirm(kind === 'main' ? 'Delete the current video?' : 'Delete the current walkthrough video?')) return;
    try {
      const data: VideoUpdateData = {
        addVideo: null,
        deletedVideoUrl: url,
        propertyId: String(pid),
        videoType: kind,
      };
      const res = (await PropertyService.videoUpdate(data)) as ApiResponse;
      if (isVideoApiSuccess(res)) {
        dispatch(kind === 'main' ? addVideoUrl(null) : addWalkthroughVideoUrl(null));
        notification.success({
          message: kind === 'main' ? 'Video deleted' : 'Walkthrough video deleted',
        });
      } else {
        notification.error({
          message: 'Video deletion failed',
          description: res?.responseInformation?.responseMessage || 'Please try again.',
        });
      }
    } catch (_) {
      notification.error({ message: 'Video deletion failed', description: 'An error occurred, please try again.' });
    }
  };

  /** Saves a newly picked video (replacing the saved one, if any). Returns false after showing an error. */
  const persistVideo = async (
    pid: string | number,
    file: File,
    kind: 'main' | 'walkthrough',
  ): Promise<boolean> => {
    const pidStr = String(pid);
    let existingOnServer: string | null = null;
    try {
      const current: any = await PropertyService.getPropertyVideo(pid);
      if (current?.responseInformation?.responseCode === 0) {
        existingOnServer =
          (kind === 'main' ? current?.result?.videoUrl : current?.result?.walkthroughVideoUrl) ?? null;
      }
    } catch (_) {
      existingOnServer = null;
    }

    let res: ApiResponse;
    if (existingOnServer) {
      const data: VideoUpdateData = {
        addVideo: file,
        deletedVideoUrl: existingOnServer,
        propertyId: pidStr,
        videoType: kind,
      };
      res = (await PropertyService.videoUpdate(data)) as ApiResponse;
    } else {
      const data: VideoUploadData = {
        video: file,
        onboardingStage: 3,
        propertyId: pidStr,
        videoType: kind,
      };
      res = (await PropertyService.videoUpload(data)) as ApiResponse;
    }
    if (!isVideoApiSuccess(res)) {
      notification.error({
        message: kind === 'main' ? 'Video upload failed' : 'Walkthrough video upload failed',
        description:
          res?.responseInformation?.responseMessage ||
          'Could not save the video. Please try again.',
      });
      return false;
    }
    return true;
  };

  /* ---- save everything ---- */

  const fetchPhotosWithRetry = async (
    pid: string,
    minCount = 1,
    attempts = 3,
    delayMs = 900,
  ): Promise<{ imageUrlList: ImageUrlList; thumb: Record<string, string> } | null> => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const res: any = await PropertyService.getPhotos(pid);
      const imageUrlList = dedupeImageUrlList(res?.result?.imageUrlList || {});
      const thumb = res?.result?.thumbnailUrl || res?.result?.thumbnail || {};
      if (countImagesInList(imageUrlList) >= minCount) {
        return { imageUrlList, thumb };
      }
      if (attempt < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    return null;
  };

  const saveAndContinue = async (): Promise<void> => {
    const pid = resolvePropertyId();
    if (!pid) {
      setError('Property ID is missing. Please go back and try again.');
      return;
    }
    if (totalPhotos < MIN_UPLOAD_COUNT) {
      setError('Please upload at least 5 images');
      return;
    }
    if (!localImages.every((img) => imageCategories[img.id])) {
      setError('Please select a category for all images');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const pidStr = String(pid);
      const knownBefore = new Set(flatten(images));
      const cover = tiles.find((t) => t.key === effectiveCoverKey) || null;

      const addImages: Record<string, File[]> = {};
      localImages.forEach((img) => {
        const cat = imageCategories[img.id] || 'other';
        if (!addImages[cat]) addImages[cat] = [];
        addImages[cat].push(img.file);
      });

      let latest: { imageUrlList: ImageUrlList; thumb: Record<string, string> } | null = null;
      const hasPhotoChanges = localImages.length > 0 || deletedImages.length > 0 || isEditMode;

      if (hasPhotoChanges) {
        let res: ApiResponse;
        if (isEditMode) {
          const existingUpdates: ImageUrlList = {};
          Object.keys(images).forEach((cat) => {
            existingUpdates[cat] = Array.from(new Set(images[cat].filter(Boolean)));
          });
          const serverCover =
            cover && cover.kind === 'server' ? { [cover.category]: cover.url } : null;
          res = (await PropertyService.updatePhotos(
            addImages,
            existingUpdates,
            {},
            serverCover,
            deletedImages.map((d) => d.url),
            pidStr,
          )) as ApiResponse;
        } else if (localImages.length > 0) {
          res = (await PropertyService.uploadPhotos(addImages, {}, pidStr)) as ApiResponse;
        } else {
          res = { result: true };
        }
        if (!res.result) {
          notification.error({
            message: 'Photo upload failed',
            description: 'Upload did not complete. Please try again.',
          });
          return;
        }
        latest = await fetchPhotosWithRetry(pidStr, 1);
      }

      const finalList: ImageUrlList = latest?.imageUrlList || dedupeImageUrlList(images);
      const serverThumbUrl = latest
        ? Object.values(latest.thumb || {})[0]
        : savedThumbUrl;

      /* Work out which saved photo is the cover. */
      let coverUrl: string | undefined;
      if (cover?.kind === 'server') {
        coverUrl = cover.url;
      } else if (cover?.kind === 'local') {
        const sameCategoryLocals = localImages.filter(
          (img) => imageCategories[img.id] === cover.category,
        );
        const index = sameCategoryLocals.findIndex((img) => img.id === cover.localId);
        const fresh = (finalList[cover.category] || []).filter((u) => !knownBefore.has(u));
        coverUrl = fresh[index] ?? fresh[fresh.length - 1];
      }
      if (!coverUrl) coverUrl = flatten(finalList)[0];

      if (coverUrl) {
        const coverCategory = Object.keys(finalList).find((c) => finalList[c].includes(coverUrl as string));
        if (coverCategory && coverUrl !== serverThumbUrl) {
          const thumbnailUrl = { [coverCategory]: coverUrl };
          const res = (await PropertyService.updatePhotos(
            {},
            finalList,
            {},
            thumbnailUrl,
            [],
            pidStr,
          )) as ApiResponse;
          if (res?.result) {
            dispatch(setThumbnail(thumbnailUrl));
          }
        }
        const thumbForStore = coverCategory ? { [coverCategory]: coverUrl } : latest?.thumb || {};
        dispatch(setImages({ imageUrlList: finalList, thumbnail: thumbForStore }));
        dispatch(addStoredImages(finalList));
      }

      /* Videos (both optional). */
      if (video && !(await persistVideo(pid, video, 'main'))) return;
      if (walkthroughVideo && !(await persistVideo(pid, walkthroughVideo, 'walkthrough'))) return;
      try {
        await PropertyService.updateLiveWalkthroughSetting(pid, allowLiveWalkthrough);
        dispatch(setAllowLiveWalkthrough(allowLiveWalkthrough));
      } catch (_) {
        // Non-blocking — navigation proceeds even if this fails
      }
      if (video || walkthroughVideo) {
        try {
          const refreshed: any = await PropertyService.getPropertyVideo(pid);
          if (refreshed?.responseInformation?.responseCode === 0) {
            dispatch(addVideoUrl(refreshed?.result?.videoUrl ?? null));
            dispatch(addWalkthroughVideoUrl(refreshed?.result?.walkthroughVideoUrl ?? null));
          }
        } catch (_) {
          // non-blocking
        }
      }

      dispatch(updateOnboardingStage(2));
      // The thumbnail is picked on the next screen, which then finishes the step.
      navigate(isEditMode ? `/host/select-thumbnail?id=${pidStr}&edit=1` : '/host/select-thumbnail');
    } catch (_) {
      notification.error({ message: 'Photo upload failed', description: 'An error occurred, please try again.' });
    } finally {
      setLoading(false);
    }
  };

  /* ---- render ---- */

  const presetKeys = ROOM_PRESETS.map((r) => r.key);
  const chipRooms = [
    ...ROOM_PRESETS.filter((r) => isRoomAllowed(r.key, roomCounts) || r.key === activeRoom),
    ...extraRooms.filter((k) => !presetKeys.includes(k)).map((k) => ({ key: k, label: roomLabel(k) })),
  ];
  const otherOptions = Object.fromEntries(
    Object.entries(IMAGE_CATEGORY_OPTIONS as Record<string, string>).filter(
      ([key]) =>
        !presetKeys.includes(key) && (isRoomAllowed(key, roomCounts) || key === activeRoom),
    ),
  );

  const pendingItems = missing > 0 ? [missingMessage] : [];

  const renderVideoField = (
    kind: 'main' | 'walkthrough',
    label: string,
    info: string | undefined,
    dropLabel: string,
  ) => {
    const picked = kind === 'main' ? video : walkthroughVideo;
    const preview = kind === 'main' ? videoPreview : walkthroughPreview;
    const saved = kind === 'main' ? videoUrl : walkthroughVideoUrl;
    const inputRef = kind === 'main' ? videoInputRef : walkthroughInputRef;
    return (
      <Field label={label} info={info}>
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) acceptVideo(file, kind);
            e.target.value = '';
          }}
        />
        {picked || saved ? (
          <VideoRow>
            <video controls src={preview || saved || undefined}>
              Your browser does not support the video tag.
            </video>
            <div className="meta">
              <div className="name">
                <span className="badge">
                  <FiVideo size={16} />
                </span>
                <span>{picked ? picked.name : 'Saved video'}</span>
              </div>
              <div className="links">
                <button type="button" className="change" onClick={() => inputRef.current?.click()}>
                  Change
                </button>
                <button
                  type="button"
                  className="remove"
                  onClick={() => (picked ? clearPickedVideo(kind) : deleteSavedVideo(kind))}
                >
                  Remove
                </button>
              </div>
            </div>
          </VideoRow>
        ) : (
          <VideoDrop type="button" onClick={() => inputRef.current?.click()}>
            <FiVideo size={20} />
            {dropLabel}
            <span className="s">MP4, MOV or WebM — up to {MAX_VIDEO_MB} MB</span>
          </VideoDrop>
        )}
      </Field>
    );
  };

  return (
    <OnboardingShell
      section={4}
      title="Add photos & video of your property"
      onBack={backHandler}
      onContinue={saveAndContinue}
      continueLabel="Continue"
      continueLoading={loading}
      continueDisabled={missing > 0}
      pending={pendingItems}
      modal={BackConfirmModal}
    >
      <Group title="Room or area">
        <ChipRow style={{ marginTop: 0 }}>
          {chipRooms.map((room) => {
            const active = !othersOpen && activeRoom === room.key;
            return (
              <Chip
                key={room.key}
                type="button"
                $active={active}
                aria-pressed={active}
                onClick={() => {
                  setActiveRoom(room.key);
                  setOthersOpen(false);
                  setError('');
                }}
              >
                {room.label}
              </Chip>
            );
          })}
          <Chip
            type="button"
            $active={othersOpen}
            aria-pressed={othersOpen}
            onClick={() => {
              setOthersOpen(true);
              setError('');
            }}
          >
            Others
          </Chip>
        </ChipRow>
        {othersOpen && (
          <CategoryDropdown
            value={otherOptions[activeRoom] ? activeRoom : ''}
            options={otherOptions}
            placeholder="Select another room or area"
            onChange={(key) => {
              setActiveRoom(key);
              setExtraRooms((prev) => (prev.includes(key) ? prev : [...prev, key]));
              setOthersOpen(false);
            }}
          />
        )}
      </Group>

      <Group
        title={`Photos in ${roomLabel(activeRoom)}`}
        note={`${totalPhotos} photo${totalPhotos === 1 ? '' : 's'} added in total`}
      >
        <PhotoGrid>
          <UploadTile {...getRootProps()} $active={isDragActive}>
            <input {...getInputProps()} />
            <span className="inner">
              <span className="ic">
                <FiImage size={16} />
              </span>
              <span className="t">Upload photos</span>
              <span className="s">JPG, PNG or WebP</span>
            </span>
          </UploadTile>
          {tiles.map((tile, index) => {
            const localIndex = localImages.findIndex((img) => img.id === tile.localId);
            return (
              <PhotoTile key={tile.key}>
                <div className="media">
                  <img src={tile.url} alt={`${roomLabel(tile.category)} photo ${index + 1}`} />
                  <div className="shade" />
                  <span className="room">{roomLabel(tile.category)}</span>
                </div>
                <div className="actions">
                  {tile.kind === 'local' ? (
                    <span style={{ display: 'flex' }}>
                      <TileButton
                        type="button"
                        aria-label="Move photo earlier"
                        disabled={localIndex <= 0}
                        onClick={() => moveLocal(tile, -1)}
                      >
                        <FiChevronLeft size={16} />
                      </TileButton>
                      <TileButton
                        type="button"
                        aria-label="Move photo later"
                        disabled={localIndex >= localImages.length - 1}
                        onClick={() => moveLocal(tile, 1)}
                      >
                        <FiChevronRight size={16} />
                      </TileButton>
                    </span>
                  ) : null}
                  <select
                    aria-label="Room or area"
                    value={tile.category}
                    onChange={(e) => handleCategoryEdit(tile, e.target.value)}
                  >
                    {Object.entries(IMAGE_CATEGORY_OPTIONS as Record<string, string>)
                      .filter(([key]) => isRoomAllowed(key, roomCounts) || key === tile.category)
                      .map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                  </select>
                  <span className="btns">
                    <TileButton
                      type="button"
                      aria-label="Remove photo"
                      $danger
                      onClick={() => handleDeleteTile(tile)}
                    >
                      <FiTrash2 size={16} />
                    </TileButton>
                  </span>
                </div>
              </PhotoTile>
            );
          })}
        </PhotoGrid>
        {missing > 0 ? <Hint $warn>{missingMessage}</Hint> : null}
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Group>

      <Group title="Video and walkthrough" largeTitle>
        <PromoBanner>
          <div className="row">
            <div className="stat">
              <span className="big">+16%</span>
              <span className="small">
                <FiTrendingUp size={12} /> More bookings
              </span>
            </div>
            <div className="copy">
              <p className="head">Listings with a video get booked more often.</p>
              <p className="sub">
                <FiMessageCircle size={16} />
                Fewer questions - guests arrive knowing exactly what to expect.
              </p>
            </div>
          </div>
        </PromoBanner>

        {renderVideoField('main', 'Property video', undefined, 'Upload video')}
        {renderVideoField(
          'walkthrough',
          'Walkthrough video',
          'A longer video guiding guests through your entire space, room by room.',
          'Upload walkthrough video',
        )}

        <SwitchRow
          label="Offer live walkthroughs on request"
          info="Allow guests to request a live video tour of your property as per its availability. Exact time can be decided between you and the guest amicably."
          note="Build guest confidence with a quick property walkthrough. Guests request a call, and you choose a convenient time to connect."
          checked={allowLiveWalkthrough}
          onChange={setAllowLiveWalkthroughLocal}
        />
      </Group>
    </OnboardingShell>
  );
};

export default PhotoUpload;
